import React, { useState, useEffect, useRef, useCallback } from 'react';

/**
 * ImageSelectionBox
 * Renders the Google Docs style selection border and 8 resize handles around the selected image.
 * Matches Image 1:
 * - Blue bounding outline
 * - 8 square blue handles (top-left, top-mid, top-right, mid-right, bot-right, bot-mid, bot-left, mid-left)
 * - Proportional corner resizing and directional edge resizing
 * - Real-time dimension preview badge
 */
const ImageSelectionBox = ({
  selectedImg,
  wrapperRef,
  contentRef,
  onResize,
  onResizeEnd,
}) => {
  const [box, setBox] = useState(null);
  const [currentDim, setCurrentDim] = useState(null);
  const rafRef = useRef(null);

  // Calculate pixel bounds of selectedImg relative to wrapperRef
  const updateBounds = useCallback(() => {
    if (!selectedImg || !wrapperRef.current) {
      setBox(null);
      return;
    }
    const imgRect = selectedImg.getBoundingClientRect();
    const wrapperRect = wrapperRef.current.getBoundingClientRect();
    const contentEl = selectedImg.closest('.rich-text-content') || contentRef?.current;
    const contentRect = contentEl ? contentEl.getBoundingClientRect() : wrapperRect;

    // If the image is scrolled completely out of view (above or below), hide the box
    if (imgRect.bottom <= contentRect.top || imgRect.top >= contentRect.bottom) {
      setBox(null);
      return;
    }

    const top = imgRect.top - wrapperRect.top;
    const left = imgRect.left - wrapperRect.left;
    const width = imgRect.width;
    const height = imgRect.height;

    setBox({ top, left, width, height });
  }, [selectedImg, wrapperRef, contentRef]);

  useEffect(() => {
    updateBounds();
    const handleScrollOrResize = () => updateBounds();
    window.addEventListener('resize', handleScrollOrResize);
    window.addEventListener('scroll', handleScrollOrResize, true);
    const wrapper = wrapperRef.current;
    if (wrapper) wrapper.addEventListener('scroll', handleScrollOrResize);

    const contentEl = selectedImg?.closest('.rich-text-content');
    if (contentEl) contentEl.addEventListener('scroll', handleScrollOrResize);

    return () => {
      window.removeEventListener('resize', handleScrollOrResize);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      if (wrapper) wrapper.removeEventListener('scroll', handleScrollOrResize);
      if (contentEl) contentEl.removeEventListener('scroll', handleScrollOrResize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [updateBounds, wrapperRef, selectedImg]);

  // Handle mousedown on any of the 8 handles
  const handleMouseDown = (e, handle) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selectedImg || !wrapperRef.current) return;

    const startX = e.clientX;
    const startY = e.clientY;
    const startW = selectedImg.offsetWidth;
    const startH = selectedImg.offsetHeight;
    const ratio = startW / (startH || 1);
    const maxW = wrapperRef.current.clientWidth - 32;

    setCurrentDim({ width: Math.round(startW), height: Math.round(startH) });

    const handleMouseMove = (moveEvent) => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);

      rafRef.current = requestAnimationFrame(() => {
        const deltaX = moveEvent.clientX - startX;
        const deltaY = moveEvent.clientY - startY;

        let newW = startW;
        let newH = startH;

        switch (handle) {
          case 'se': // Bottom-right corner (proportional)
            newW = Math.max(50, Math.min(maxW, startW + deltaX));
            newH = Math.round(newW / ratio);
            break;
          case 'sw': // Bottom-left corner (proportional)
            newW = Math.max(50, Math.min(maxW, startW - deltaX));
            newH = Math.round(newW / ratio);
            break;
          case 'ne': // Top-right corner (proportional)
            newW = Math.max(50, Math.min(maxW, startW + deltaX));
            newH = Math.round(newW / ratio);
            break;
          case 'nw': // Top-left corner (proportional)
            newW = Math.max(50, Math.min(maxW, startW - deltaX));
            newH = Math.round(newW / ratio);
            break;
          case 'e': // Middle-right edge (width only)
            newW = Math.max(50, Math.min(maxW, startW + deltaX));
            break;
          case 'w': // Middle-left edge (width only)
            newW = Math.max(50, Math.min(maxW, startW - deltaX));
            break;
          case 's': // Bottom-center edge (height only)
            newH = Math.max(40, startH + deltaY);
            break;
          case 'n': // Top-center edge (height only)
            newH = Math.max(40, startH - deltaY);
            break;
          default:
            break;
        }

        newW = Math.round(newW);
        newH = Math.round(newH);

        selectedImg.style.width = `${newW}px`;
        if (handle === 's' || handle === 'n' || handle === 'se' || handle === 'sw' || handle === 'ne' || handle === 'nw') {
          selectedImg.style.height = `${newH}px`;
        } else {
          selectedImg.style.height = 'auto';
        }
        selectedImg.style.maxWidth = '100%';
        selectedImg.style.objectFit = 'contain';

        setCurrentDim({ width: newW, height: newH });
        updateBounds();

        if (onResize) {
          onResize({ width: newW, height: newH });
        }
      });
    };

    const handleMouseUp = () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      setCurrentDim(null);
      updateBounds();
      if (onResizeEnd) {
        onResizeEnd();
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  if (!box || !selectedImg) return null;

  // The 8 Google Docs handles definition
  const handles = [
    { id: 'nw', cursor: 'nwse-resize', style: { top: -5, left: -5 } },
    { id: 'n', cursor: 'ns-resize', style: { top: -5, left: 'calc(50% - 5px)' } },
    { id: 'ne', cursor: 'nesw-resize', style: { top: -5, right: -5 } },
    { id: 'e', cursor: 'ew-resize', style: { top: 'calc(50% - 5px)', right: -5 } },
    { id: 'se', cursor: 'nwse-resize', style: { bottom: -5, right: -5 } },
    { id: 's', cursor: 'ns-resize', style: { bottom: -5, left: 'calc(50% - 5px)' } },
    { id: 'sw', cursor: 'nesw-resize', style: { bottom: -5, left: -5 } },
    { id: 'w', cursor: 'ew-resize', style: { top: 'calc(50% - 5px)', left: -5 } },
  ];

  return (
    <div
      className="rte-gdocs-selection-box"
      style={{
        top: `${box.top}px`,
        left: `${box.left}px`,
        width: `${box.width}px`,
        height: `${box.height}px`,
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Live dimensions badge while resizing */}
      {currentDim && (
        <div className="rte-gdocs-dimension-badge">
          {currentDim.width} × {currentDim.height} px
        </div>
      )}

      {/* 8 Google Docs Resize Handles */}
      {handles.map((h) => (
        <div
          key={h.id}
          className={`rte-gdocs-handle rte-gdocs-handle-${h.id}`}
          style={{ ...h.style, cursor: h.cursor }}
          onMouseDown={(e) => handleMouseDown(e, h.id)}
          title={`Resize image (${h.id.toUpperCase()})`}
        />
      ))}
    </div>
  );
};

export default ImageSelectionBox;
